import React from 'react';
import {Check, X} from 'lucide-react';
import FaqList from '../../components/site/FaqList';
import FeatureGrid from '../../components/site/FeatureGrid';
import HeroSection from '../../components/site/HeroSection';
import SectionTitle from '../../components/site/SectionTitle';
import SiteLink from '../../components/site/SiteLink';
import {useSiteContent} from './siteContent';
import pageStyles from './SitePages.module.css';
import tableStyles from './MembershipPage.module.css';

/**
 * 官网定价页：等级对比表 + 付费功能简介 + 开通引导 + FAQ
 * @author page
 * @date 2026-10-15
 */
const MembershipPage: React.FC = () => {
    const {membershipPage, heroMetrics, secondaryCta} = useSiteContent();
    const {hero, sectionTitles, priceLabels, tiers, featureIntros, faqIntro, faqItems} = membershipPage;

    return (
        <div className={pageStyles.page}>
            <section className={pageStyles.section}>
                <HeroSection
                    description={hero.description}
                    eyebrow={hero.eyebrow}
                    metrics={heroMetrics}
                    panelDescription={hero.panelDescription}
                    panelLabel={hero.panelLabel}
                    panelTitle={hero.panelTitle}
                    title={hero.title}
                />
            </section>

            <section className={pageStyles.section}>
                <SectionTitle
                    description={sectionTitles.compare.description}
                    eyebrow={sectionTitles.compare.eyebrow}
                    title={sectionTitles.compare.title}
                />
                <div className="site-section"/>
                <div className={tableStyles.tableWrap}>
                    <table className={tableStyles.table}>
                        <thead>
                        <tr>
                            <th/>
                            {tiers.map(tier => (
                                <th key={tier.name}
                                    className={tier.highlight ? tableStyles.highlightCol : undefined}>
                                    <span className={tableStyles.tierName}>{tier.name}</span>
                                    <span className={tableStyles.tierTagline}>{tier.tagline}</span>
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>{priceLabels.monthly}</td>
                            {tiers.map(tier => (
                                <td key={tier.name}
                                    className={tier.highlight ? tableStyles.highlightCol : undefined}>
                                    <span className={tableStyles.price}>{tier.monthlyPrice}</span>
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td>{priceLabels.yearly}</td>
                            {tiers.map(tier => (
                                <td key={tier.name}
                                    className={tier.highlight ? tableStyles.highlightCol : undefined}>
                                    <span className={tableStyles.price}>{tier.yearlyPriceMonthly}</span>
                                </td>
                            ))}
                        </tr>
                        {tiers[0].features.map((feat, idx) => (
                            <tr key={feat.label}>
                                <td>{feat.label}</td>
                                {tiers.map(tier => {
                                    const f = tier.features[idx];
                                    return (
                                        <td key={tier.name}
                                            className={tier.highlight ? tableStyles.highlightCol : undefined}>
                                            {f.enabled
                                                ? <Check size={18} className={tableStyles.enabledIcon}/>
                                                : <X size={18} className={tableStyles.disabledIcon}/>}
                                            {f.enabled && f.note
                                                ? <span className={tableStyles.featNote}>{f.note}</span>
                                                : null}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className={pageStyles.section}>
                <SectionTitle
                    description={sectionTitles.features.description}
                    eyebrow={sectionTitles.features.eyebrow}
                    title={sectionTitles.features.title}
                />
                <div className="site-section"/>
                <FeatureGrid items={featureIntros} columns={4}/>
            </section>

            <section className={pageStyles.section}>
                <article className={`site-card ${pageStyles.ctaPanel}`}>
                    <div>
                        <span className="site-eyebrow">{sectionTitles.cta.eyebrow}</span>
                        <h3>{sectionTitles.cta.title}</h3>
                        <p>{sectionTitles.cta.description}</p>
                    </div>
                    <div className={pageStyles.ctaActions}>
                        <SiteLink className={pageStyles.ctaButton} to={secondaryCta.path}>
                            {secondaryCta.label}
                        </SiteLink>
                    </div>
                </article>
            </section>

            <section className={pageStyles.section}>
                <div className={pageStyles.faqWrap}>
                    <article className={`site-card ${pageStyles.faqCard}`}>
                        <span className="site-eyebrow">{faqIntro.eyebrow}</span>
                        <h3>{faqIntro.title}</h3>
                        <p>{faqIntro.description}</p>
                    </article>
                    <article className={`site-card ${pageStyles.faqCard}`}>
                        <FaqList items={faqItems}/>
                    </article>
                </div>
            </section>
        </div>
    );
};

export default MembershipPage;
